/**!
 * Created by Leonardo Cheslacki on 29/01/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import WithType from './../WithType';

class StageZoom extends StageController {
    /**
     *
     * @param props
     * @param main
     * @returns {StageZoom}
     */
    constructor(props, main) {
        super(props);

        // if (StageZoom._this instanceof StageZoom) {
        //     return StageZoom._this;
        // }

        this.props = props;
        this.main = main;

        this.state = {
            original: {
                x: 0,
                y: 0,
                scale_x: 0,
                scale_y: 0
            },
            touches: {
                one: {
                    clicked_x: 0,
                    clicked_y: 0
                },
                two: {
                    clicked_x: 0,
                    clicked_y: 0
                }
            },
            ui: {
                name: null,
                active: false
            },
            control: {
                speed_x: 0,
                speed_y: 0,
                clicked_x: 0,
                clicked_y: 0
            }
        };

        this.$isTouch = false;
        this.$isDragging = false;
        this.$isDraggingZoom = false;
        this.$isDraggingZoomState = null;

        this.$thisFrame = null;

        StageZoom._this = this;
    };

    /**
     *
     * @param key
     * @param value
     * @param type
     */
    setControl = (key, value, type = null) => {
        const {control} = this.state;

        switch (type) {
            case 'increment':
                control[key] += value;
                break;
            case 'decrement':
                control[key] -= value;
                break;
            case 'times':
                control[key] *= value;
                break;
            default:
                control[key] = value;
                break;
        }
    };

    /**
     *
     * @param type
     */
    setScale = (type) => {
        const
            {original} = this.state,
            {zoom_step, max_scale} = this.props,
            {occ_sprite} = this.getFrame();

        let result = (occ_sprite.scaleX * 3) * zoom_step;

        if (type === 'increment') {
            occ_sprite.scaleX = occ_sprite.scaleY = (occ_sprite.scaleX + result);
        } else if (type === 'decrement') {
            occ_sprite.scaleX = occ_sprite.scaleY = (occ_sprite.scaleX - result);
        }

        if (occ_sprite.scaleX < original.scale_x) {
            occ_sprite.scaleX = original.scale_x;
            occ_sprite.scaleY = original.scale_y;
        } else if (occ_sprite.scaleX > max_scale) {
            occ_sprite.scaleX = occ_sprite.scaleY = max_scale;
        }
    };

    lerp = (v1, v2, friction) => {
        return friction * v1 + (1 - friction) * v2;
    }

    $functions = {
        update: () => {
            const _this = StageZoom._this;
            const
                {stage, stage: {canvas}, occ_sprite} = _this.getFrame(),
                {
                    displacement,
                    friction,
                    bounds_friction,
                    nudge,
                    check_bounds,
                    check_bounds_when_dragging,
                    check_bounds_when_pressing
                } = _this.props,
                {
                    original: {x, y},
                    ui: {active, name},
                    control: {
                        clicked_x,
                        clicked_y,
                        speed_x,
                        speed_y
                    }
                } = _this.state;

            if (active) {
                switch (name) {
                    case 'btn_more':
                        _this.setScale('increment');
                        break;
                    case 'btn_less':
                        _this.setScale('decrement');
                        break;
                    case 'btn_left':
                        _this.setControl('speed_x', nudge, 'decrement');
                        break;
                    case 'btn_up':
                        _this.setControl('speed_y', nudge, 'decrement');
                        break;
                    case 'btn_right':
                        _this.setControl('speed_x', nudge, 'increment');
                        break;
                    case 'btn_down':
                        _this.setControl('speed_y', nudge, 'increment');
                        break;
                }
            }

            if (_this.$isDraggingZoom) {
                _this.$isDraggingZoomState ? _this.setScale('increment') : _this.setScale('decrement');
            }

            if (_this.$isDragging && !_this.$isDraggingZoom) {
                if (_this.$isTouch) {
                    _this.setControl('speed_x', (_this.state.target_touch_one.big_clientX.toString() - clicked_x));
                    _this.setControl('speed_y', (_this.state.target_touch_one.big_clientY.toString() - clicked_y));
                    _this.setControl('clicked_x', _this.state.target_touch_one.big_clientX.toString());
                    _this.setControl('clicked_y', _this.state.target_touch_one.big_clientY.toString());
                } else {
                    _this.setControl('speed_x', (stage.mouseX - clicked_x));
                    _this.setControl('speed_y', (stage.mouseY - clicked_y));
                    _this.setControl('clicked_x', stage.mouseX);
                    _this.setControl('clicked_y', stage.mouseY);
                }
            }

            _this.setControl('speed_x', friction, 'times');
            _this.setControl('speed_y', friction, 'times');

            occ_sprite.x += speed_x;
            occ_sprite.y += speed_y;

            if ((!check_bounds) || (_this.$isDragging && !check_bounds_when_dragging) || (active && !check_bounds_when_pressing)) {
                return;
            }

            let
                bounds = occ_sprite.nominalBounds,
                width = (bounds.width * occ_sprite.scaleX),
                height = (bounds.height * occ_sprite.scaleY),
                left = (occ_sprite.x - width * displacement),
                right = (occ_sprite.x + width * displacement),
                top = (occ_sprite.y - height * displacement),
                bottom = (occ_sprite.y + height * displacement),
                canvas_width = (canvas.width / stage.scaleX),
                canvas_height = (canvas.height / stage.scaleY);

            if (width >= canvas_width) {
                if (left > 0) {
                    occ_sprite.x = _this.lerp(occ_sprite.x, (width * displacement), bounds_friction);
                } else if (right < canvas_width) {
                    occ_sprite.x = _this.lerp(occ_sprite.x, canvas_width - width * displacement, bounds_friction);
                }
            } else {
                occ_sprite.x = _this.lerp(occ_sprite.x, x, bounds_friction);
            }

            if (height >= canvas_height) {
                if (top > 0) {
                    occ_sprite.y = _this.lerp(occ_sprite.y, height * displacement, bounds_friction);
                } else if (bottom < canvas_height) {
                    occ_sprite.y = _this.lerp(occ_sprite.y, canvas_height - height * displacement, bounds_friction);
                }
            } else {
                occ_sprite.y = _this.lerp(occ_sprite.y, y, bounds_friction);
            }

            //occ_sprite.stage.update();
        },

        handleZoomMouseDown: () => {
            const _this = StageZoom._this;
            const {stage} = _this.getFrame();

            _this.setControl('clicked_x', stage.mouseX);
            _this.setControl('clicked_y', stage.mouseY);

            _this.$isDragging = true;
            _this.$isDraggingZoom = false;
        },

        /**
         *
         * @param event
         * @param big_clientX
         * @param big_clientY
         */
        handleZoomTouchStart: (event, {big_clientX, big_clientY}) => {
            const _this = StageZoom._this;
            let
                target_touch_one = event.targetTouches.item(0),
                target_touch_two = event.targetTouches.item(1);

            _this.setControl('clicked_x', big_clientX.toString());
            _this.setControl('clicked_y', big_clientY.toString());

            _this.setState({
                ..._this.state,
                touches: {
                    one: {
                        clicked_x: target_touch_one.clientX
                    },
                    two: {
                        clicked_x: (!_this.isEmpty(target_touch_two) ? target_touch_two.clientX : 0)
                    }
                }
            });

            _this.$isTouch = true;
        },

        /**
         *
         * @param event
         * @param state
         */
        handleZoomTouchMove: (event, state) => {
            const _this = StageZoom._this;
            let
                target_touch_one = event.targetTouches.item(0),
                target_touch_two = event.targetTouches.item(1);

            _this.setState({
                ..._this.state,
                target_touch_one: state
            });

            if (!_this.isEmpty(target_touch_two)) {
                const {touches: {one, two}} = _this.state;

                let start = 0, end = 0;

                _this.$isDragging = false;
                _this.$isDraggingZoom = true;

                if (one.clicked_x > two.clicked_x) {
                    start = (one.clicked_x - two.clicked_x);
                } else {
                    start = (two.clicked_x - one.clicked_x);
                }

                if (target_touch_one.clientX > target_touch_two.clientX) {
                    end = (target_touch_one.clientX - target_touch_two.clientX);
                } else {
                    end = (target_touch_two.clientX - target_touch_one.clientX);
                }

                _this.$isDraggingZoomState = (start < end);
            } else {
                _this.$isDragging = true;
                _this.$isDraggingZoom = false;
            }
        },

        /**
         *
         * @param event
         */
        handleZoomSprite: (event) => {
            const _this = StageZoom._this;
            let {name} = event.target;

            _this.inArray(['btn_more', 'btn_less'], name) && _this.setState({
                ..._this.state,
                ui: {name: name, active: true}
            });
        },

        /**
         *
         * @param event
         */
        handleMoveSprite: (event) => {
            const _this = StageZoom._this;
            let {name} = event.target;

            _this.inArray(['btn_left', 'btn_up', 'btn_right', 'btn_down'], name) && _this.setState({
                ..._this.state,
                ui: {name: name, active: true}
            });
        },

        /**
         *
         */
        handleResetSprite: () => {
            const _this = StageZoom._this;
            const
                {occ_sprite} = _this.getFrame(),
                {original: {x, y, scale_x, scale_y}} = _this.state;

            occ_sprite.x = x;
            occ_sprite.y = y;
            occ_sprite.scaleX = scale_x;
            occ_sprite.scaleY = scale_y;
        },

        /**
         *
         * @param event
         */
        handleZoomWheelSprite: (event) => {
            const _this = StageZoom._this;
            _this.setScale((_this.toIntegerOrFloat(event.deltaY * -0.01) > 0) ? 'increment' : 'decrement');
        },

        /**
         *
         */
        handleResetUi: () => {
            const _this = StageZoom._this;
            _this.$isTouch = false;
            _this.$isDragging = false;
            _this.$isDraggingZoom = false;
            _this.$isDraggingZoomState = null;
            _this.setState({..._this.state, ui: {name: null, active: false}});
        }
    };

    start = () => {
        const {stage, stage: {canvas}} = this.getFrame();

        const
            {
                occ_ui: {
                    btn_more,
                    btn_less,
                    btn_left,
                    btn_up,
                    btn_right,
                    btn_down,
                    btn_reset
                }
            } = this.get(),
            {occ_sprite} = this.getFrame();

        let composition = this.getComposition(this.props.current_name);
        this.isEmpty(composition) && (composition = this.getComposition(this.props.current_page));

        const {properties} = composition.getLibrary();

        this.destroyTicker('_handleZoom');

        createjs.Touch.enable(stage);
        createjs.Ticker.timingMode = createjs.Ticker.RAF;

        let ticker = createjs.Ticker.on('tick', this.$functions.update);

        this.setReferenceTicker('_handleZoom', ticker);

        this._handleStageMouseUp && stage.off('stagemouseup', this._handleStageMouseUp);
        this._handleStageMouseUp = stage.on('stagemouseup', this.$functions.handleResetUi);

        const handle = (event, {type, state}) => {
            switch (type) {
                case '_MOUSE_DOWN_':
                    this.$functions.handleZoomMouseDown();
                    break;
                case '_TOUCH_START_':
                    this.$functions.handleZoomTouchStart(event, state);
                    break;
                case '_TOUCH_MOVE_':
                    this.$functions.handleZoomTouchMove(event, state);
                    break;
                case '_TOUCH_END_':
                    this.$functions.handleResetUi();
                    break;
                default:
            }
        };

        AllTap.$supportTouchCanvas({occ: occ_sprite, properties: properties, callback: handle}).mainTouch();

        this.setHandleMouseDown(btn_more, this.$functions.handleZoomSprite);

        this.setHandleMouseDown(btn_less, this.$functions.handleZoomSprite);

        this.setHandleMouseDown(btn_left, this.$functions.handleMoveSprite);

        this.setHandleMouseDown(btn_up, this.$functions.handleMoveSprite);

        this.setHandleMouseDown(btn_right, this.$functions.handleMoveSprite);

        this.setHandleMouseDown(btn_down, this.$functions.handleMoveSprite);

        this.setHandleMouseDown(btn_reset, this.$functions.handleResetSprite);

        if (createjs.BrowserDetect.isIOS) {
            this.removeListener('mousewheel', 'handle-zoom-wheel-sprite-ios', () => {
                this.addListener(canvas, 'mousewheel', 'handle-zoom-wheel-sprite-ios', this.$functions.handleZoomWheelSprite);
            });
        } else {
            this.removeListener('wheel', 'handle-zoom-wheel-sprite', () => {
                this.addListener(canvas, 'wheel', 'handle-zoom-wheel-sprite', this.$functions.handleZoomWheelSprite);
            });
        }
    };

    /**
     *
     */
    destroy = () => {
        this.$functions.handleResetUi();
        this.$functions.handleResetSprite();
        this.start();
    };

    /**
     *
     */
    startZoom = () => {
        const {stage, occ_sprite: {x, y, scaleX, scaleY}} = this.getFrame();

        this.setState({
            ...this.state,
            original: {
                x: x,
                y: y,
                scale_x: scaleX,
                scale_y: scaleY
            }
        }, () => {
            stage.enableMouseOver();
            stage.mouseMoveOutside = true;
            stage.on('drawstart', this.start, null, true);
        });
    };

    /**
     *
     */
    mainZoom = () => {
        this.startZoom();
    };
}

/**
 *
 * @type {{}}
 */
StageZoom.propTypes = {
    current_name: PropTypes.string,
    friction: PropTypes.number,
    bounds_friction: PropTypes.number,
    nudge: PropTypes.number,
    zoom_step: PropTypes.number,
    max_scale: PropTypes.number,
    displacement: PropTypes.number,
    check_bounds: PropTypes.bool,
    check_bounds_when_dragging: PropTypes.bool,
    check_bounds_when_pressing: PropTypes.bool,
    support_touch: PropTypes.bool
};

/**
 *
 * @type {{}}
 */
StageZoom.defaultProps = {
    current_name: null,
    friction: 0.85,
    bounds_friction: 0.85,
    nudge: 5,
    zoom_step: 0.01,
    max_scale: 1,
    displacement: 0.5,
    check_bounds: true,
    check_bounds_when_dragging: false,
    check_bounds_when_pressing: false,
    support_touch: true
};

/**
 *
 * @type {{}}
 * @private
 */
StageZoom._this = {};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageZoom, {className: 'StageZoom'});
